/**
 * Roster claim-link onboarding page.
 *
 * A student lands here from a per-student link (`/claim/:token`) that was
 * pre-provisioned from a class roster. The account already exists (group +
 * team assigned); this page lets the student set their own email + password,
 * which promotes the account to a full user, signs them in, and routes them
 * into their story. See plans/2026-06-21-roster-claim-link-onboarding.md.
 */

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container, Stack, Text, Title, TextInput, PasswordInput, Button, Paper, Anchor, Loader, Alert, Group, Badge,
} from '@mantine/core'
import { IconMail, IconLock, IconUser, IconAlertCircle } from '@tabler/icons-react'
import { useAuth } from '../hooks/useAuth'
import { authService } from '../services/authService'

const inputStyles = {
  input: {
    background: 'rgba(10, 22, 40, 0.8)',
    border: '1px solid rgba(0, 255, 255, 0.15)',
    color: '#e0e8f0',
  },
  label: { color: 'var(--mantine-color-gray-4)' },
}

export default function Claim() {
  const { token } = useParams()
  const navigate = useNavigate()
  const { claim } = useAuth()

  const [status, setStatus] = useState('loading') // loading | ready | claimed | invalid
  const [invite, setInvite] = useState(null)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [confirmError, setConfirmError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let active = true
    authService
      .getClaim(token)
      .then((res) => {
        if (!active) return
        const data = res.data
        setInvite(data)
        setStatus(data.already_claimed ? 'claimed' : 'ready')
        if (data.roster_name) setName(rosterToDisplayName(data.roster_name))
      })
      .catch((err) => {
        if (!active) return
        // 404 (unknown or consumed token) reads as "already used / invalid".
        setStatus(err.response?.status === 404 ? 'invalid' : 'invalid')
      })
    return () => {
      active = false
    }
  }, [token])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (password !== confirmPassword) {
      setConfirmError('Passwords do not match')
      return
    }
    setConfirmError('')
    setSubmitting(true)
    const result = await claim(token, {
      email,
      password,
      passwordConfirmation: confirmPassword,
      name,
    })
    setSubmitting(false)
    // On success, useAuth().claim has already navigated. On failure it showed a
    // notification; the token is preserved server-side so the student can retry.
    if (!result.success && /already been claimed/i.test(result.error || '')) {
      setStatus('claimed')
    }
  }

  if (status === 'loading') {
    return (
      <Container size="xs" py="xl">
        <Stack align="center" gap="lg" style={{ minHeight: '50vh', justifyContent: 'center' }}>
          <Loader color="cyan" />
          <Text c="dimmed">Looking up your invitation…</Text>
        </Stack>
      </Container>
    )
  }

  if (status === 'invalid' || status === 'claimed') {
    const claimed = status === 'claimed'
    return (
      <Container size="xs" py="xl">
        <Stack align="center" gap="lg" style={{ minHeight: '50vh', justifyContent: 'center' }}>
          <Title order={2} ff="Orbitron, sans-serif" c="cyan.4">
            {claimed ? 'Already set up' : 'Invitation not found'}
          </Title>
          <Paper p="xl" className="glass" w="100%">
            <Stack gap="md">
              <Alert icon={<IconAlertCircle size={16} />} color={claimed ? 'cyan' : 'red'} variant="light">
                {claimed
                  ? 'This invitation has already been used. Log in with the email and password you set.'
                  : 'This link is invalid or has already been used. If you have already set up your account, log in below.'}
              </Alert>
              <Button fullWidth size="md" onClick={() => navigate('/login')}>
                Go to log in
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    )
  }

  // status === 'ready'
  return (
    <Container size="xs" py="xl">
      <Stack align="center" gap="lg" style={{ minHeight: '50vh', justifyContent: 'center' }}>
        <Title order={2} ff="Orbitron, sans-serif" c="cyan.4" ta="center">
          {invite?.roster_name ? `Welcome, ${rosterToDisplayName(invite.roster_name)}` : 'Set up your account'}
        </Title>

        {(invite?.group_name || invite?.team_name) && (
          <Group gap="xs" justify="center">
            {invite?.group_name && <Badge color="cyan" variant="light">{invite.group_name}</Badge>}
            {invite?.team_name && <Badge color="grape" variant="light">{invite.team_name}</Badge>}
          </Group>
        )}

        <Text size="sm" c="dimmed" ta="center">
          Choose an email and password to finish setting up your account.
        </Text>

        <Paper p="xl" className="glass" w="100%">
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label="Name"
                placeholder="Your name"
                leftSection={<IconUser size={16} />}
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                required
                styles={inputStyles}
              />
              <TextInput
                label="Email"
                placeholder="your@email.com"
                leftSection={<IconMail size={16} />}
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                type="email"
                required
                styles={inputStyles}
              />
              <PasswordInput
                label="Password"
                placeholder="Choose a password"
                leftSection={<IconLock size={16} />}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
                minLength={6}
                autoComplete="new-password"
                styles={inputStyles}
              />
              <PasswordInput
                label="Confirm Password"
                placeholder="Re-enter your password"
                leftSection={<IconLock size={16} />}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                required
                minLength={6}
                error={confirmError}
                autoComplete="new-password"
                styles={inputStyles}
              />
              <Button type="submit" loading={submitting} fullWidth size="md">
                Enter the simulation
              </Button>
            </Stack>
          </form>
        </Paper>

        <Text size="sm" c="dimmed">
          Already set up?{' '}
          <Anchor c="cyan.4" onClick={() => navigate('/login')}>
            Log in
          </Anchor>
        </Text>
      </Stack>
    </Container>
  )
}

// Roster names arrive as "LASTNAME, Firstname". Present them naturally as
// "Firstname LASTNAME" for the greeting + as the default editable name.
function rosterToDisplayName(rosterName) {
  const parts = rosterName.split(',').map((s) => s.trim())
  if (parts.length === 2 && parts[1]) return `${parts[1]} ${parts[0]}`
  return rosterName
}
